import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { RotateCcw, Wallet, AlertTriangle, CalendarCheck, ArrowUpRight, ArrowDownRight, ShoppingBag, Package, CalendarClock, WashingMachine, PackageOpen, ChevronLeft, ChevronRight, Banknote, Landmark } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { id as localeId } from 'date-fns/locale';
import api, { apiError } from '../lib/api';
import { formatIDR, formatDate, formatDateLong, todayStr, methodLabel } from '../lib/format';
import { useAuth } from '../context/AuthContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { FotoMini } from '../components/FotoMini';
import { HapusCari } from '../components/HapusCari';
import { kaliModal } from './PerformaKoleksi';

const DONUT_COLORS = ['#3B281C', '#7B5E48', '#B58A62', '#C9A96E', '#8a8574', '#5A3E28'];
const RANGES = [
  { v: '7d', l: '7 Hari' },
  { v: '30d', l: '30 Hari' },
  { v: '90d', l: '3 Bulan' },
  { v: '1y', l: '1 Tahun' },
];

function Kpi({ testid, label, value, sub, icon: Icon, onClick }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className="bg-white rounded-2xl border border-border shadow-soft p-5 card-lift text-left w-full cursor-pointer active:scale-[0.98]">
      <div className="flex items-center justify-between">
        <p className="text-[11px] uppercase tracking-wider text-mocha font-semibold">{label}</p>
        <Icon size={16} className="text-caramel" />
      </div>
      <p className="font-display text-2xl md:text-3xl text-brown-dark mt-2 tracking-tight">{value}</p>
      {sub && <div className="text-xs text-mocha mt-1">{sub}</div>}
    </button>
  );
}

function MethodRows({ byMethod, extraLabel, extraValue, extraTestid }) {
return (
  <div className="space-y-1.5 mt-3">
    {['cash', 'transfer', 'qris'].map((m) => (
      <div key={m} className="flex justify-between text-sm">
        <span className="text-mocha">{methodLabel(m)}</span>
        <span className="font-semibold" data-testid={`fin-${m}`}>{formatIDR(byMethod[m] || 0)}</span>
      </div>
    ))}
    {Object.keys(byMethod).filter((k) => !['cash', 'transfer', 'qris'].includes(k)).map((m) => (
      <div key={m} className="flex justify-between text-sm">
        <span className="text-mocha">{methodLabel(m)}</span>
        <span className="font-semibold">{formatIDR(byMethod[m])}</span>
      </div>
    ))}
    <div className="flex justify-between text-sm border-t border-dashed border-border pt-1.5">
      <span className="text-danger font-semibold">{extraLabel}</span>
      <span className="font-bold text-danger" data-testid={extraTestid}>{formatIDR(extraValue)}</span>
    </div>
  </div>
);
}

function MonthlyFinance() {
  const [month, setMonth] = useState(todayStr().slice(0, 7));
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/dashboard/monthly-finance?month=${month}`).then((r) => setData(r.data));
  }, [month]);

  const shiftMonth = (n) => {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(y, m - 1 + n, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  if (!data) return null;
  const monthLabel = format(parseISO(`${month}-01`), 'MMMM yyyy', { locale: localeId });


  return (
    <div data-testid="monthly-finance">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-xl text-ink">Keuangan Bulan Ini</h2>
        <div className="flex items-center gap-1">
          <button data-testid="fin-prev-month" onClick={() => shiftMonth(-1)} className="p-1.5 rounded-lg hover:bg-white text-brown border border-border bg-white"><ChevronLeft size={15} /></button>
          <span className="text-sm font-bold text-brown-dark capitalize px-2 min-w-[110px] text-center" data-testid="fin-month-label">{monthLabel}</span>
          <button data-testid="fin-next-month" onClick={() => shiftMonth(1)} className="p-1.5 rounded-lg hover:bg-white text-brown border border-border bg-white"><ChevronRight size={15} /></button>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-border shadow-soft p-5" data-testid="omset-bulan-ini-card">
          <div className="flex items-center gap-2">
            <Banknote size={17} className="text-caramel" />
            <p className="text-[11px] uppercase tracking-wider text-mocha font-semibold">Omset {monthLabel} — per tanggal pakai</p>
          </div>
          <p className="font-display text-2xl md:text-3xl text-brown-dark mt-2" data-testid="fin-omset-total">{formatIDR(data.omset.total)}</p>
          <p className="text-[11px] text-mocha">{data.omset.orders} order pakai bulan ini</p>
          <MethodRows byMethod={data.omset.by_method} extraLabel="Belum dibayar (sisa)" extraValue={data.omset.sisa} extraTestid="fin-omset-sisa" />
        </div>
        <div className="bg-white rounded-2xl border border-border shadow-soft p-5" data-testid="uang-masuk-card">
          <div className="flex items-center gap-2">
            <Landmark size={17} className="text-caramel" />
            <p className="text-[11px] uppercase tracking-wider text-mocha font-semibold">Uang Masuk {monthLabel} — per tanggal bayar</p>
          </div>
          <p className="font-display text-2xl md:text-3xl text-brown-dark mt-2" data-testid="fin-inflow-total">{formatIDR(data.uang_masuk.total)}</p>
          <p className="text-[11px] text-mocha">kas benar-benar diterima bulan ini</p>
          <MethodRows byMethod={data.uang_masuk.by_method} extraLabel="Piutang (sisa order bulan ini)" extraValue={data.uang_masuk.piutang} extraTestid="fin-piutang" />
          {data.uang_masuk.by_order_month && Object.keys(data.uang_masuk.by_order_month).length > 0 && (
            <div className="mt-3 pt-3 border-t border-border" data-testid="fin-inflow-by-order-month">
              <p className="text-[10px] uppercase tracking-wider text-mocha font-bold mb-1.5">Uang masuk ini untuk orderan bulan:</p>
              {Object.entries(data.uang_masuk.by_order_month).map(([m, v]) => (
                <div key={m} className="flex justify-between text-xs py-0.5" data-testid={`fin-inflow-month-${m}`}>
                  <span className={m === data.month ? 'font-bold text-brown' : 'text-mocha'}>
                    {new Date(m + '-02').toLocaleDateString('id-ID', { month: 'long', year: 'numeric' })}
                    {m === data.month ? ' (bulan ini)' : ''}
                  </span>
                  <span className="font-semibold text-ink">{formatIDR(v)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <p className="text-[11px] text-mocha mt-3 leading-relaxed" data-testid="finance-explainer">
        Cara baca: <b>Omset</b> = nilai order berdasarkan tanggal pakai. <b>Uang Masuk</b> = kas yang benar-benar diterima berdasarkan tanggal pembayaran (bisa termasuk DP order bulan lalu/depan). Keduanya menghitung kalender penuh 1 bulan (tgl 1 s/d akhir bulan) — di menu Laporan pilih periode "Bulan Ini" agar angkanya sama.
      </p>
    </div>
  );
}

function StockCheck() {
  const [q, setQ] = useState('');
  const [d, setD] = useState(todayStr());
  const [products, setProducts] = useState([]);
  const [sugs, setSugs] = useState([]);
  const [picked, setPicked] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/products').then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const ql = q.trim().toLowerCase();
    if (!ql || picked) { setSugs([]); return; }
    const t = setTimeout(() => {
      setSugs(products.filter((p) => p.name.toLowerCase().includes(ql) || p.code.toLowerCase().includes(ql)).slice(0, 8));
    }, 150);
    return () => clearTimeout(t);
  }, [q, products, picked]);

  const check = async (p, dateStr) => {
    setPicked(p);
    setSugs([]);
    setQ(`${p.code} ${p.name}`);
    setLoading(true);
    try {
      const r = await api.get(`/stock/inspect?product_id=${p.id}&date=${dateStr}`);
      setResult(r.data);
    } catch {
      setResult(null);
    }
    setLoading(false);
  };

  const STATE_LBL = { sewa: 'disewa', booking: 'dibooking', laundry: 'di laundry' };

  return (
    <div className="bg-white rounded-2xl border border-border shadow-soft p-4" data-testid="stock-check">
      <p className="text-xs font-bold uppercase tracking-wider text-mocha mb-2.5">Cek Ketersediaan Stok — ketik nama produk, pilih dari daftar, lalu atur tanggal pakai</p>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <input
            data-testid="stock-check-q"
            value={q}
            onChange={(e) => { setQ(e.target.value); setPicked(null); }}
            placeholder="mis. Kebaya Janggan…"
            className="w-full rounded-xl border border-border bg-ivory/50 pl-3.5 pr-9 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brown/30"
          />
          <HapusCari show={!!q} onClear={() => { setQ(''); setPicked(null); }} testid="stock-check-clear" />
          {sugs.length > 0 && (
            <div className="absolute z-30 mt-1 w-full bg-white border border-border rounded-xl shadow-lift max-h-56 overflow-y-auto" data-testid="stock-check-suggestions">
              {sugs.map((p) => (
                <button
                  key={p.id}
                  data-testid={`stock-check-sug-${p.code}`}
                  onClick={() => check(p, d)}
                  className="w-full text-left px-4 py-2.5 hover:bg-ivory border-b border-border/50 last:border-0 flex items-center gap-2.5"
                >
                  <FotoMini product={p} size={32} testid={`stock-check-foto-${p.code}`} />
                  <span className="flex-1 min-w-0">
                    <p className="text-sm font-bold"><span className="text-brown">{p.code}</span> {p.name}</p>
                    <p className="text-xs text-mocha">{p.category} • total stok {p.total_stock}</p>
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
        <input
          data-testid="stock-check-date"
          type="date"
          value={d}
          onChange={(e) => { setD(e.target.value); if (picked) check(picked, e.target.value); }}
          className="rounded-xl border border-border bg-ivory/50 px-3 py-2.5 text-sm outline-none"
        />
      </div>
      {loading && <p className="text-xs text-mocha mt-2">Memeriksa ketersediaan…</p>}
      {result && !loading && (
        <div className="mt-3 space-y-2" data-testid="stock-check-result">
          <div className="flex items-start gap-3">
            <FotoMini product={result.product} size={44} testid="stock-check-foto-hasil" />
            <p className="text-xs text-mocha">
              Ketersediaan <b className="text-ink">{result.product.name}</b> untuk tanggal <b className="text-ink">{formatDate(result.date)}</b>:
            </p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(result.sizes).map(([s, v]) => (
              <span
                key={s}
                data-testid={`stock-check-size-${s.replace(/[^A-Za-z0-9]+/g, '-')}`}
                className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${v.available > 0 ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'}`}
              >
                {s}: tersedia {v.available}/{v.total}
              </span>
            ))}
          </div>
          {Object.entries(result.sizes).some(([, v]) => v.holders.length > 0 || v.damaged > 0) && (
            <div className="rounded-xl bg-ivory border border-border px-3.5 py-2.5 space-y-1" data-testid="stock-check-holders">
              {Object.entries(result.sizes).map(([s, v]) => (
                <div key={s}>
                  {v.damaged > 0 && <p className="text-[11px] text-danger font-semibold">{s}: {v.damaged} pcs rusak/perbaikan</p>}
                  {v.holders.map((h, i) => (
                    <p key={i} className="text-[11px] text-mocha" data-testid={`stock-check-holder-${s.replace(/[^A-Za-z0-9]+/g, '-')}-${i}`}>
                      {s}: {STATE_LBL[h.state]} <b className="text-ink">{h.customer}</b> {h.pcs} pcs <span className="text-brown">({h.ref})</span>
                    </p>
                  ))}
                </div>
              ))}
            </div>
          )}
          {Object.values(result.sizes).every((v) => v.available === v.total - v.damaged) && (
            <p className="text-xs text-success font-semibold" data-testid="stock-check-all-ready">Semua ukuran ready di tanggal ini — aman untuk disewakan.</p>
          )}
        </div>
      )}
    </div>
  );
}

function KpiSheet({ type, onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (!type) return;
    setRows(null);
    const urls = {
      revenue: '/dashboard/today-revenue', orders: '/dashboard/today-orders',
      rented: '/dashboard/out-now', attention: '/dashboard/attention',
    };
    api.get(urls[type]).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [type]);

  const titles = { revenue: 'Omset Hari Ini — per pembayaran', orders: 'Order Hari Ini', rented: 'Barang Sedang Disewa', attention: 'Perlu Perhatian' };

  return (
    <Sheet open={!!type} onOpenChange={onClose}>
      <SheetContent side="bottom" className="rounded-t-3xl bg-ivory max-h-[80vh] overflow-y-auto" data-testid="kpi-sheet">
        <SheetHeader>
          <SheetTitle className="font-display text-brown-dark" data-testid="kpi-sheet-title">{titles[type]}</SheetTitle>
        </SheetHeader>
        {!rows ? (
          <p className="text-sm text-mocha py-8 text-center">Memuat…</p>
        ) : (
          <div className="py-4 space-y-2.5">
            {type === 'revenue' && (rows.length === 0 ? (
              <p className="text-sm text-mocha text-center py-6">Belum ada uang masuk hari ini.</p>
            ) : rows.map((p) => (
              <Link key={p.id} to={`/transaksi/${p.transaction_id}`} data-testid={`kpi-revenue-${p.transaction_number}`}
                className="block bg-white rounded-2xl border border-border p-4 hover:bg-ivory/60">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-semibold text-ink text-sm">{p.customer_name}</p>
                    <p className="text-xs text-mocha">{p.transaction_number} • {p.kind === 'pelunasan' ? 'Pelunasan' : 'DP'} • {p.method}</p>
                  </div>
                  <p className="font-display font-bold text-success">{formatIDR(p.amount)}</p>
                </div>
              </Link>
            )))}
            {type === 'orders' && (rows.length === 0 ? (
              <p className="text-sm text-mocha text-center py-6">Belum ada order baru hari ini.</p>
            ) : rows.map((t) => (
              <Link key={t.id} to={`/transaksi/${t.id}`} data-testid={`kpi-order-${t.transaction_number}`}
                className="block bg-white rounded-2xl border border-border p-4 hover:bg-ivory/60">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-semibold text-ink text-sm">{t.customer_name}</p>
                    <p className="text-xs text-mocha">{t.transaction_number} • {t.pcs} pcs • pakai {formatDate(t.use_date)}</p>
                  </div>
                  <p className="font-display font-bold text-brown">{formatIDR(t.total)}</p>
                </div>
              </Link>
            )))}
            {type === 'rented' && (rows.length === 0 ? (
              <p className="text-sm text-mocha text-center py-6">Tidak ada barang yang sedang disewa.</p>
            ) : rows.map((r, i) => (
              <Link key={`${r.transaction_id}-${i}`} to={`/transaksi/${r.transaction_id}`} data-testid={`kpi-rented-${r.transaction_number}-${i}`}
                className="block bg-white rounded-2xl border border-border p-4 hover:bg-ivory/60">
                <div className="flex justify-between items-start gap-3">
                  <div>
                    <p className="font-semibold text-ink text-sm">{r.product_name}</p>
                    <p className="text-xs text-mocha mt-0.5">{r.qty} pcs ({r.sizes}) • {r.customer_name}</p>
                  </div>
                  <p className="text-[11px] font-bold text-brown whitespace-nowrap">Kembali {formatDate(r.return_date)}</p>
                </div>
              </Link>
            )))}
            {type === 'attention' && (
              <div className="space-y-4">
                {rows.overdue?.length > 0 && (
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-danger font-bold mb-2">Terlambat Kembali</p>
                    {rows.overdue.map((t) => (
                      <Link key={t.id} to={`/transaksi/${t.id}`} data-testid={`kpi-overdue-${t.transaction_number}`}
                        className="block bg-white rounded-2xl border border-danger/30 p-4 mb-2 hover:bg-danger/5">
                        <p className="font-semibold text-ink text-sm">{t.customer_name}</p>
                        <p className="text-xs text-danger font-bold">{t.transaction_number} • telat {t.days} hari (kembali {formatDate(t.return_date)})</p>
                      </Link>
                    ))}
                  </div>
                )}
                {rows.laundry?.length > 0 && (
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-orange-700 font-bold mb-2">Masih di Laundry</p>
                    {rows.laundry.map((t) => (
                      <Link key={t.id} to={`/laundry`} data-testid={`kpi-laundry-${t.transaction_number}`}
                        className="block bg-white rounded-2xl border border-border p-4 mb-2 hover:bg-ivory/60">
                        <p className="font-semibold text-ink text-sm">{t.customer_name}</p>
                        <p className="text-xs text-mocha">{t.transaction_number} • {t.ready_date >= '2099' ? 'tanpa batas tanggal' : `estimasi ready ${formatDate(t.ready_date)}`}</p>
                      </Link>
                    ))}
                  </div>
                )}
                {rows.damaged?.length > 0 && (
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-danger font-bold mb-2">Barang Rusak</p>
                    {rows.damaged.map((p) => (
                      <div key={p.code} data-testid={`kpi-damaged-${p.code}`} className="bg-white rounded-2xl border border-border p-4 mb-2">
                        <p className="font-semibold text-ink text-sm">{p.name}</p>
                        <p className="text-xs text-mocha">{p.code} • {p.pcs} pcs rusak</p>
                      </div>
                    ))}
                  </div>
                )}
                {!rows.overdue?.length && !rows.laundry?.length && !rows.damaged?.length && (
                  <p className="text-sm text-mocha text-center py-6">Tidak ada yang perlu perhatian. Semua aman.</p>
                )}
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function StaffDashboard() {  const [mon, setMon] = useState(null);
  const [loadErr, setLoadErr] = useState('');
  const load = useCallback(() => {
    setLoadErr('');
    api.get('/monitoring').then((r) => setMon(r.data)).catch((err) => setLoadErr(apiError(err, 'Gagal memuat dashboard')));
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  if (loadErr) return (
    <div className="py-20 text-center" data-testid="staff-dashboard-error">
      <p className="text-sm text-danger font-semibold">{loadErr}</p>
      <button data-testid="staff-dashboard-retry" onClick={load}
        className="mt-4 rounded-xl bg-brown text-white px-6 py-2.5 text-sm font-bold">Coba Lagi</button>
    </div>
  );
  if (!mon) return <div className="py-20 text-center text-mocha text-sm">Memuat dashboard…</div>;

  const cards = [
    { testid: 'staff-packing', label: 'Perlu Packing', value: mon.counts.scheduled, icon: Package, to: '/pemantauan', tone: 'text-gold-dark' },
    { testid: 'staff-due-today', label: 'Kembali Hari Ini', value: mon.counts.due_today, icon: CalendarClock, to: '/pemantauan', tone: 'text-[#8a5f1e]' },
    { testid: 'staff-out', label: 'Sedang Disewa', value: mon.counts.out_now, icon: PackageOpen, to: '/pemantauan', tone: 'text-brown' },
    { testid: 'staff-laundry', label: 'Di Laundry', value: mon.counts.laundry, icon: WashingMachine, to: '/pemantauan', tone: 'text-orange-700' },
    { testid: 'staff-overdue', label: 'Telat Kembali', value: mon.counts.overdue, icon: AlertTriangle, to: '/pemantauan', tone: 'text-danger' },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-staff">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {cards.map((c) => (
          <Link key={c.testid} to={c.to} data-testid={c.testid} className="bg-white rounded-2xl border border-border shadow-soft p-4 text-center card-lift block">
            <c.icon size={20} className={`mx-auto ${c.tone}`} />
            <p className="font-display text-2xl text-ink mt-1.5">{c.value}</p>
            <p className="text-[10px] uppercase tracking-wider text-mocha font-semibold mt-0.5">{c.label}</p>
          </Link>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-border shadow-soft p-5" data-testid="staff-packing-list">
        <h2 className="font-display text-xl text-ink mb-4">Daftar Perlu Packing</h2>
        {mon.scheduled.length === 0 ? (
          <p className="text-sm text-mocha">Tidak ada order terjadwal.</p>
        ) : (
          <div className="space-y-2.5">
            {mon.scheduled.slice(0, 8).map((t) => (
              <Link key={t.id} to={`/transaksi/${t.id}`} data-testid={`packing-row-${t.transaction_number}`} className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-ivory px-4 py-3 hover:bg-muted transition-colors">
                <div>
                  <p className="text-sm font-bold text-brown">{t.transaction_number} <span className="text-ink">— {t.customer_name}</span></p>
                  <p className="text-xs text-mocha">
                    Pakai {formatDate(t.use_date)} • ambil {formatDate(t.pickup_date)} • {t.pcs} pcs
                  </p>
                </div>
                <div className="flex gap-1.5 flex-wrap">
                  {t.rows.slice(0, 3).map((r, i) => (
                    <span key={i} className="rounded-full bg-brown/10 text-brown text-[10px] font-bold px-2.5 py-0.5">{r.product_code} ×{r.qty}</span>
                  ))}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-border shadow-soft p-5">
        <h2 className="font-display text-xl text-ink mb-4">Kembali Hari Ini</h2>
        {mon.due_today.length === 0 ? (
          <p className="text-sm text-mocha">Tidak ada yang harus kembali hari ini.</p>
        ) : (
          <div className="space-y-2.5">
            {mon.due_today.map((t) => (
              <Link key={t.id} to={`/transaksi/${t.id}`} data-testid={`due-row-${t.transaction_number}`} className="flex items-center justify-between rounded-xl bg-ivory px-4 py-3 hover:bg-muted transition-colors">
                <p className="text-sm font-bold text-brown">{t.transaction_number} <span className="text-ink">— {t.customer_name}</span></p>
                <p className="text-xs text-mocha">{t.pcs} pcs</p>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Kartu ringkas Performa Koleksi (owner). Gagal memuat = kartu tidak tampil,
// dashboard tetap jalan.
function KoleksiRingkas() {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get('/koleksi/performa?ringkas=1').then((r) => setD(r.data)).catch(() => setD(null));
  }, []);
  if (!d) return null;
  const s = d.ringkasan;
  const daftar = (rows, kosong, testid) => (rows.length === 0 ? <p className="text-xs text-mocha">{kosong}</p> : (
    <ul className="space-y-1" data-testid={testid}>
      {rows.slice(0, 3).map((r) => (
        <li key={r.code} className="flex justify-between gap-2 text-sm">
          <span className="truncate"><b className="text-brown-dark">{r.code}</b> <span className="text-mocha">{r.name}</span></span>
          <span className="shrink-0 text-xs text-mocha">{r.pcs} pcs / stok {r.stok}</span>
        </li>
      ))}
    </ul>
  ));
  return (
    <div className="bg-white rounded-2xl border border-border shadow-soft p-5" data-testid="dash-koleksi">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div>
          <h2 className="font-display text-xl text-ink">Performa Koleksi</h2>
          <p className="text-xs text-mocha">Data sewa {d.umur_data_hari} hari · {s.laris} laris · {s.sepi + s.belum_pernah} sepi/belum pernah</p>
        </div>
        <Link to="/performa-koleksi" data-testid="dash-koleksi-link" className="text-sm font-semibold text-brown hover:underline">Lihat semua →</Link>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-wider text-success font-semibold mb-1.5">Paling laris</p>
          {daftar(d.tambah, 'Belum ada yang laris.', 'dash-koleksi-laris')}
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-wider text-danger font-semibold mb-1.5">Tahan pengadaan</p>
          {daftar(d.tahan, 'Tidak ada.', 'dash-koleksi-tahan')}
        </div>
        <div className="rounded-xl bg-ivory p-3">
          <p className="text-[11px] uppercase tracking-wider text-mocha font-semibold">Balik modal</p>
          <p className="font-display text-2xl text-brown-dark mt-1" data-testid="dash-koleksi-balik">{kaliModal(s.balik_modal)}</p>
          <p className="text-xs text-mocha mt-1">{s.balik_modal === null ? 'Isi harga beli dulu di menu Performa Koleksi.' : `Modal terisi ${s.modal_terisi} dari ${s.modal_perlu} kode`}</p>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const isOwner = user?.role !== 'karyawan';
  const [data, setData] = useState(null);
  const [range, setRange] = useState('7d');
  const [kpiOpen, setKpiOpen] = useState(null);

  useEffect(() => {
    api.get(`/dashboard?range=${range}`).then((r) => setData(r.data));
  }, [range]);

  if (!data) {
    return <div className="py-20 text-center text-mocha text-sm">Memuat dashboard…</div>;
  }

  const pct = data.revenue_yesterday > 0
    ? Math.round(((data.revenue_today - data.revenue_yesterday) / data.revenue_yesterday) * 100)
    : null;
  const a = data.actions;

  const actionRows = [
    { level: 'danger', count: a.returns_due, text: `${a.returns_due} order harus kembali hari ini / telat`, to: '/pemantauan', testid: 'action-returns-due' },
    { level: 'warning', count: a.unpaid, text: `${a.unpaid} order belum lunas`, to: '/transaksi', testid: 'action-unpaid' },
    { level: 'warning', count: a.laundry, text: `${a.laundry} order barang masih di laundry`, to: '/pemantauan', testid: 'action-laundry' },
    { level: 'success', count: a.bookings_tomorrow, text: `${a.bookings_tomorrow} booking besok`, to: '/booking', testid: 'action-bookings-tomorrow' },
  ];
  const dotCls = { danger: 'bg-danger', warning: 'bg-warning', success: 'bg-success' };

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl md:text-4xl text-ink tracking-tight">Selamat datang, {user?.name}</h1>
          <p className="text-sm text-mocha mt-1">{formatDateLong(todayStr())}</p>
        </div>
        <Link
          to="/transaksi/baru"
          data-testid="dashboard-new-transaction-button"
          className="rounded-xl bg-brown text-white px-5 py-2.5 text-sm font-semibold hover:bg-brown-med transition-colors"
        >
          + Order Baru
        </Link>
      </div>

      <StockCheck />

      {isOwner ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          <Kpi
            testid="kpi-omset"
            label="Omset Hari Ini"
            value={formatIDR(data.revenue_today)}
            icon={Wallet}
            onClick={() => setKpiOpen('revenue')}
            sub={
              pct !== null ? (
                <span className={`inline-flex items-center gap-1 font-semibold ${pct >= 0 ? 'text-success' : 'text-danger'}`}>
                  {pct >= 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                  {Math.abs(pct)}% vs kemarin
                </span>
              ) : (
                <span>Kemarin {formatIDR(data.revenue_yesterday)}</span>
              )
            }
          />
          <Kpi testid="kpi-transaksi" label="Order Hari Ini" value={data.transactions_today} icon={ShoppingBag} sub={<span>klik untuk daftar nama</span>} onClick={() => setKpiOpen('orders')} />
          <Kpi testid="kpi-disewa" label="Sedang Disewa" value={`${data.rented_count} pcs`} icon={RotateCcw} sub={<span>klik untuk rincian barang</span>} onClick={() => setKpiOpen('rented')} />
          <Kpi
            testid="kpi-perhatian"
            label="Perlu Perhatian"
            value={data.attention_count}
            icon={AlertTriangle}
            onClick={() => setKpiOpen('attention')}
            sub={<span>{data.damaged_pcs > 0 ? `${data.damaged_pcs} pcs rusak • ` : ''}telat / laundry</span>}
          />
        </div>
      ) : (
        <StaffDashboard />
      )}

      <KpiSheet type={kpiOpen} onClose={() => setKpiOpen(null)} />

      {isOwner && <MonthlyFinance />}

      {isOwner && <KoleksiRingkas />}

      {isOwner && (
        <div className="grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 bg-white rounded-2xl border border-border shadow-soft p-5" data-testid="revenue-chart-card">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
              <h2 className="font-display text-xl text-ink">Pendapatan</h2>
              <div className="flex gap-1.5" data-testid="chart-range-filters">
                {RANGES.map((r) => (
                  <button
                    key={r.v}
                    data-testid={`chart-range-${r.v}`}
                    onClick={() => setRange(r.v)}
                    className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                      range === r.v ? 'bg-brown text-white' : 'bg-ivory text-mocha hover:bg-muted'
                    }`}
                  >
                    {r.l}
                  </button>
                ))}
              </div>
            </div>
            <div className="h-56 md:h-64 min-h-[224px] min-w-[200px]">
              <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={120}>
                <AreaChart data={data.chart} margin={{ left: 0, right: 8, top: 4, bottom: 0 }}>
                  <defs>
                    <linearGradient id="goldFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#C9A96E" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#C9A96E" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="date" tickFormatter={(d) => formatDate(d).slice(0, 6)} tick={{ fontSize: 10, fill: '#766C64' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                  <YAxis tickFormatter={(v) => (v >= 1000000 ? `${v / 1000000}jt` : v >= 1000 ? `${v / 1000}rb` : v)} tick={{ fontSize: 10, fill: '#766C64' }} axisLine={false} tickLine={false} width={44} />
                  <Tooltip formatter={(v) => formatIDR(v)} labelFormatter={(d) => formatDate(d)} />
                  <Area type="monotone" dataKey="revenue" stroke="#5A3E28" strokeWidth={2.5} fill="url(#goldFill)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-border shadow-soft p-5" data-testid="action-today-card">
            <h2 className="font-display text-xl text-ink mb-4">Aksi Hari Ini</h2>
            <div className="space-y-2.5">
              {actionRows.map((r) => (
                <Link
                  key={r.testid}
                  to={r.to}
                  data-testid={r.testid}
                  className="flex items-center gap-3 rounded-xl bg-ivory px-4 py-3 hover:bg-muted transition-colors"
                >
                  <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${dotCls[r.level]}`} />
                  <span className={`text-sm ${r.count > 0 ? 'font-semibold text-ink' : 'text-mocha'}`}>{r.text}</span>
                </Link>
              ))}
            </div>

            <h3 className="font-display text-lg text-ink mt-6 mb-3">Sumber Order</h3>
            {data.sources.length === 0 ? (
              <p className="text-xs text-mocha">Belum ada order pada rentang ini.</p>
            ) : (
              <div className="flex items-center gap-3">
                <div className="w-28 h-28 shrink-0" data-testid="source-donut">
                  <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={120}>
                    <PieChart>
                      <Pie data={data.sources} dataKey="value" nameKey="name" innerRadius={30} outerRadius={52} paddingAngle={3}>
                        {data.sources.map((s, i) => (
                          <Cell key={s.name} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-1.5">
                  {data.sources.map((s, i) => (
                    <div key={s.name} className="flex items-center gap-2 text-xs">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                      <span className="text-mocha">{s.name}</span>
                      <span className="font-bold text-ink">{s.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-border shadow-soft p-5" data-testid="upcoming-bookings-card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl text-ink">Booking Mendatang</h2>
          <Link to="/booking" data-testid="see-all-bookings-link" className="text-xs font-semibold text-brown hover:underline">
            Lihat semua
          </Link>
        </div>
        {data.upcoming_bookings.length === 0 ? (
          <p className="text-sm text-mocha">Belum ada booking mendatang.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.upcoming_bookings.map((b) => (
              <div key={b.id} data-testid={`booking-card-${b.id}`} className="rounded-xl bg-ivory p-4 border border-border">
                <div className="flex items-center gap-2 text-brown font-semibold text-sm">
                  <CalendarCheck size={15} className="text-caramel" />
                  {b.customer_name}
                  {b.order_ref && <span className="text-[10px] text-mocha font-normal">#{b.order_ref}</span>}
                </div>
                <p className="text-xs text-mocha mt-1">
                  Pakai {formatDate(b.use_date)} • {b.pcs} pcs
                </p>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {(b.rows || []).slice(0, 4).map((r, i) => (
                    <span key={i} className="rounded-full bg-brown/10 text-brown text-[10px] font-bold px-2.5 py-0.5">
                      {r.product_code} ×{r.qty}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
